import Splitting from 'splitting';
import {
  getTextDigitalGradientElement,
  getTextDigitalStaticHandoffRange,
  getTextDigitalMarkerWhitespaceBoundary,
  hasTextDigitalHandoffStateChanged,
  restoreTextDigitalMarkerWhitespace,
  resolveTextDigitalHandoffWord,
  updateTextDigitalGradientWordStyles,
  isTextDigitalHandoffActive,
  TEXT_DIGITAL_FILL_TRIGGER_ID,
} from '../../utils/text-digital-fill-transition.utils.js';

const initializedTextDigitalFill = new WeakSet();
const TEXT_DIGITAL_FILL_SCROLL_FACTOR = 0.35;
const TEXT_DIGITAL_FILL_PARALLAX = {
  desktop: { from: 150, to: -80 },
};
const TEXT_DIGITAL_FILL_SCRUB = {
  desktop: 0.6,
};

const getTextDigitalFillElements = (scope) => {
  if (!scope) {
    return [];
  }

  if (
    typeof scope.matches === 'function' &&
    scope.matches('[data-text-digital-fill]')
  ) {
    return [scope];
  }

  if (typeof scope.querySelectorAll !== 'function') {
    return [];
  }

  return Array.from(scope.querySelectorAll('[data-text-digital-fill]'));
};

const setWordsProgress = (words, progress) => {
  words.forEach((word) => {
    word.style.setProperty('--text-digital-fill-word-progress', progress);
  });
};

const getWordsByRows = (words) => {
  const rowThreshold = 8;

  return words
    .reduce((rows, word) => {
      const rect = word.getBoundingClientRect();
      const row = rows.find((item) => Math.abs(item.top - rect.top) <= rowThreshold);

      if (row) {
        row.words.push({ word, left: rect.left });
        row.top = (row.top + rect.top) / 2;
        return rows;
      }

      rows.push({
        top: rect.top,
        words: [{ word, left: rect.left }],
      });

      return rows;
    }, [])
    .sort((a, b) => a.top - b.top)
    .map((row) =>
      row.words.sort((a, b) => a.left - b.left).map((item) => item.word)
    );
};

window.textDigitalFillAnimation = function textDigitalFillAnimation(
  scope = document
) {
  const elements = getTextDigitalFillElements(scope);

  if (!elements.length) {
    return;
  }

  const canAnimate =
    typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined';

  if (canAnimate) {
    gsap.registerPlugin(ScrollTrigger);
  }

  elements.forEach((element) => {
    if (initializedTextDigitalFill.has(element)) {
      return;
    }

    initializedTextDigitalFill.add(element);

    const section = element.closest('.text-digital-sec');
    const parallaxTarget =
      section?.querySelector('.text-digital-sec__wrap') || element;
    const pinTarget = section || parallaxTarget;
    const accessibleText = element.textContent.trim().replace(/\s+/g, ' ');

    if (accessibleText) {
      element.setAttribute('aria-label', accessibleText);
    }

    const originalHTML = element.innerHTML;
    const originalClass = element.getAttribute('class');
    const originalStyle = element.getAttribute('style');
    const restoreContent = () => {
      element.innerHTML = originalHTML;
      for (const [name, value] of [['class', originalClass], ['style', originalStyle]]) {
        if (value === null) {
          element.removeAttribute(name);
        } else {
          element.setAttribute(name, value);
        }
      }
      delete element['🍌'];
      section?.classList.remove('is-text-digital-handoff');
      if (section?.classList.contains('is-bg-changes')) {
        window.syncBgChangeTheme?.(section);
      }
    };
    const showStaticText = (hasReducedMotion = true) => {
      const { element: gradientElement, isLegacy } = getTextDigitalGradientElement(element);
      element.classList.add('is-text-digital-fill-static');
      section?.classList.remove('is-text-digital-handoff');
      if (canAnimate && section) {
        const watermark = section.querySelector('.text-digital-sec__watermark-logo');
        if (watermark && !hasReducedMotion) {
          gsap.fromTo(watermark, { y: 48 }, {
            y: -48,
            ease: 'none',
            scrollTrigger: {
              id: 'text-digital-watermark-parallax',
              trigger: section,
              start: 'top 75%',
              end: 'bottom 25%',
              scrub: 0.6,
            },
          });
        }
        const range = getTextDigitalStaticHandoffRange(
          element,
          gradientElement,
          isLegacy ? element.dataset.textDigitalHandoffWord : undefined
        );
        if (range) {
          const syncHandoff = (self) => {
            const active = self.scroll() >= self.start;
            if (section.classList.contains('is-text-digital-handoff') === active) {
              return;
            }
            section.classList.toggle('is-text-digital-handoff', active);
            if (section.classList.contains('is-bg-changes')) {
              window.syncBgChangeTheme?.(section);
            }
          };
          ScrollTrigger.create({
            id: 'text-digital-static-handoff',
            trigger: section,
            start: () => {
              const rect = range.getBoundingClientRect();
              return window.scrollY + rect.top + rect.height / 2 - window.innerHeight / 2;
            },
            end: 'bottom top',
            onUpdate: syncHandoff,
            onRefresh: syncHandoff,
          });
        }
      }
      return restoreContent;
    };

    if (!canAnimate) {
      showStaticText();
      return;
    }

    const media = gsap.matchMedia();
    media.add({
      compact: '(max-width: 1024px)',
      desktop: '(min-width: 1025px)',
      reduced: '(prefers-reduced-motion: reduce)',
    }, (context) => {
      if (context.conditions.compact) {
        return showStaticText(context.conditions.reduced);
      }
      const hasReducedMotion = context.conditions.reduced;
      const { element: gradientElement, isLegacy: isLegacyGradient } =
        getTextDigitalGradientElement(element);
      const markerWhitespaceBoundary = getTextDigitalMarkerWhitespaceBoundary(
        element,
        gradientElement
      );

      Splitting({
        target: element,
        by: 'words',
        force: true,
      });

      restoreTextDigitalMarkerWhitespace(markerWhitespaceBoundary);

      const words = Array.from(element.querySelectorAll('.word'));
      const gradientElements = Array.from(
        element.querySelectorAll('[data-text-digital-gradient]')
      );

      gradientElements.forEach(updateTextDigitalGradientWordStyles);

      if (!words.length) {
        element.classList.add('is-text-digital-fill-complete');
        return restoreContent;
      }

      words.forEach((word) => {
        if (!word.dataset.word) {
          word.dataset.word = word.textContent;
        }
      });

      element.classList.add('is-text-digital-fill-init');
      setWordsProgress(words, hasReducedMotion ? '100%' : '0%');

      if (hasReducedMotion) {
        element.classList.add('is-text-digital-fill-complete');
        return restoreContent;
      }

      const trigger = section || element;
      const rows = getWordsByRows(words);
      const orderedWords = rows.flat();
      const handoffWord = resolveTextDigitalHandoffWord(
        orderedWords,
        element,
        gradientElement,
        isLegacyGradient ? element.dataset.textDigitalHandoffWord : undefined
      );
      const wordDuration = 1;
      const rowPause = 0.45;
      const parallax = TEXT_DIGITAL_FILL_PARALLAX.desktop;
      const scrub = TEXT_DIGITAL_FILL_SCRUB.desktop;
      const scrollDistance = Math.max(
        window.innerHeight * 1.85,
        orderedWords.length * 96 + Math.max(rows.length - 1, 0) * 140
      ) * TEXT_DIGITAL_FILL_SCROLL_FACTOR;
      let timelinePosition = 0;
      let handoffEnd;
      let isTextDigitalHandoff = false;

      const fillTimeline = gsap.timeline({
        scrollTrigger: {
          id: TEXT_DIGITAL_FILL_TRIGGER_ID,
          trigger,
          start: 'top top',
          end: `+=${scrollDistance}`,
          pin: pinTarget,
          pinSpacing: true,
          anticipatePin: 1,
          scrub,
          refreshPriority: 2,
          invalidateOnRefresh: true,
          onRefresh: () => {
            gradientElements.forEach(updateTextDigitalGradientWordStyles);
          },
        },
      });

      fillTimeline.fromTo(
        parallaxTarget,
        {
          y: parallax.from,
        },
        {
          y: parallax.to,
          duration: orderedWords.length * wordDuration + rows.length * rowPause,
          ease: 'none',
        },
        0
      );

      rows.forEach((row) => {
        row.forEach((word) => {
          fillTimeline.to(
            word,
            {
              '--text-digital-fill-word-progress': '100%',
              duration: wordDuration,
              ease: 'none',
            },
            timelinePosition
          );

          timelinePosition += wordDuration;

          if (word === handoffWord) {
            handoffEnd = timelinePosition;
          }
        });

        timelinePosition += rowPause;
      });

      fillTimeline.eventCallback('onUpdate', () => {
        if (!fillTimeline.scrollTrigger?.isActive) {
          return;
        }

        const nextTextDigitalHandoff = isTextDigitalHandoffActive(
          fillTimeline.time(),
          handoffEnd
        );

        if (
          !hasTextDigitalHandoffStateChanged(
            isTextDigitalHandoff,
            nextTextDigitalHandoff
          )
        ) {
          return;
        }

        isTextDigitalHandoff = nextTextDigitalHandoff;
        section?.classList.toggle(
          'is-text-digital-handoff',
          isTextDigitalHandoff
        );
        window.syncBgChangeTheme?.(section);
      });
      return restoreContent;
    });
  });
};

if (!document.querySelector('.bold-loader-new')) {
  window.textDigitalFillAnimation();
}
